// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useLocation } from "react-router-dom";

const FILTERS = [
  { key: "all", label: "All", category: null },
  { key: "live", label: "Live", category: "live" },
  { key: "others", label: "Others", category: "others" },
];

const FilterPill = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${
      selected
        ? "bg-general-primary text-general-primary-foreground hover:bg-hoverPrimary"
        : "bg-general-secondary text-general-secondary-foreground hover:bg-[#DCCCE2]"
    } min-h-8 px-3 py-1.5 rounded-full flex justify-center items-center gap-1.5 ani shrink-0`}
  >
    <span className="text-center text-sm font-medium leading-5">{label}</span>
  </button>
);

const ClientSelectDropDown = ({
  checked,
  options,
  setChecked,
  apiLoad = false,
  label = null,
  defaultOpen = false,
}) => {
  const pathParts = useLocation().pathname.split("/");
  const [isDropdownOpen, setDropdownOpen] = useState(defaultOpen);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const clientRefs = useRef({});
  const clientListRef = useRef(null);

  const handleOptionClick = (option) => {
    setChecked(option);
  };

  const isOptionSelected = (option) =>
    !!checked && checked.clientname === option.clientname;

  // Panel is ~330px tall (search + filters + list). Flip above the trigger when
  // there isn't room below, so opening it never scrolls the page.
  const PANEL_MAX_HEIGHT = 330;

  const toggleDropdown = () => {
    if (apiLoad) return;
    if (!isDropdownOpen) {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenUpward(spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow);
      }
    }
    setDropdownOpen(!isDropdownOpen);
  };

  const selectOption = (option) => {
    localStorage.setItem("selectedClient", JSON.stringify(option));
    handleOptionClick(option);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  const visibleOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (options || []).filter((option) => {
      const matchesCategory =
        !categoryFilter || option.category === categoryFilter;
      const matchesQuery =
        !query || option.clientname?.toLowerCase().includes(query);
      return matchesCategory && matchesQuery;
    });
  }, [options, searchQuery, categoryFilter]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        !dropdownRef.current?.contains(event.target) &&
        !event.target.closest?.(".sidebar")
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;
    inputRef.current?.focus();
    clientRefs.current[checked?.clientname]?.scrollIntoView({
      block: "nearest",
    });
  }, [isDropdownOpen, checked]);

  useEffect(() => {
    const cF = localStorage.getItem("categoryFilter");
    setCategoryFilter(!cF || cF === "null" ? null : cF);
  }, []);

  useEffect(() => {
    if (!pathParts.includes("hwai-login")) return;
    const demoClient = options?.find(
      (client) => client.name === "Demo" || client.clientname === "Demo"
    );
    if (demoClient && !isOptionSelected(demoClient)) {
      handleOptionClick(demoClient);
    }
  }, [options]);

  return (
    <div
      ref={dropdownRef}
      className="panel-hover font-geist w-full flex flex-col justify-start items-start gap-1 relative"
    >
      {!!label && (
        <label className="text-general-foreground text-sm font-normal leading-5">
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
        className={`${
          isDropdownOpen
            ? "outline-unofficial-border-3 shadow-[inset_0px_0px_0px_3px_rgba(212,212,212,1.00)]"
            : "outline-general-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
        } ${
          apiLoad ? "cursor-progress" : "cursor-pointer"
        } self-stretch min-h-9 pl-3 pr-2 py-2 bg-general-input rounded-lg outline outline-1 outline-offset-[-1px] ani flex justify-start items-center gap-2 overflow-hidden`}
      >
        <span className="flex-1 flex justify-start items-center gap-1 overflow-hidden">
          <span
            className={`${
              apiLoad || !checked?.clientname
                ? "text-general-muted-foreground"
                : "text-general-foreground"
            } text-sm font-normal leading-5 line-clamp-1 text-left`}
          >
            {apiLoad ? "Loading..." : checked?.clientname || "Select client"}
          </span>
        </span>
        <MdKeyboardArrowDown
          size={16}
          className={`${isDropdownOpen ? "rotate-180" : ""} ${
            apiLoad ? "opacity-0" : ""
          } shrink-0 ani text-general-muted-foreground`}
        />
      </button>

      <div
        className={`${
          isDropdownOpen
            ? ""
            : "translate-y-[-8px] opacity-0 pointer-events-none select-none"
        } ${
          openUpward
            ? "bottom-full mb-1 origin-bottom"
            : "top-full mt-1 origin-top"
        } ani absolute left-0 w-full z-30 bg-card-card rounded-lg shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10)] shadow-md outline outline-1 outline-general-border flex flex-col justify-start items-start overflow-hidden`}
      >
        <div className="self-stretch min-h-10 px-3 py-2.5 flex justify-start items-center gap-2 overflow-hidden">
          <AiOutlineSearch
            size={16}
            className="shrink-0 text-general-muted-foreground"
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDropdownOpen(false);
              } else if (e.key === "Enter" && visibleOptions.length > 0) {
                e.preventDefault();
                selectOption(visibleOptions[0]);
              }
            }}
            className="flex-1 min-w-0 w-full bg-transparent text-general-foreground placeholder-general-muted-foreground text-sm font-normal leading-5 outline-none"
          />
        </div>

        <div className="self-stretch h-px bg-general-border shrink-0" />

        <div className="self-stretch pl-3 pr-2 py-1 flex justify-start items-center gap-1 overflow-hidden">
          {FILTERS.map((filter) => (
            <FilterPill
              key={filter.key}
              label={filter.label}
              selected={categoryFilter === filter.category}
              onClick={() => {
                setCategoryFilter(filter.category);
                localStorage.setItem("categoryFilter", filter.category);
              }}
            />
          ))}
        </div>

        <div className="self-stretch h-px bg-general-border shrink-0" />

        <div
          role="listbox"
          className="panel-hover-scrollbar self-stretch max-h-[232px] px-0.5 py-1 flex flex-col justify-start items-start overflow-y-auto"
        >
          {visibleOptions.length === 0 ? (
            <div className="self-stretch min-h-8 px-2 py-1.5 rounded-md flex justify-start items-center gap-2">
              <div className="flex-1 text-left text-general-muted-foreground text-sm font-normal leading-5">
                No clients found
              </div>
            </div>
          ) : (
            visibleOptions.map((option) => (
              <div
                key={option._id || option.clientname}
                ref={(el) => {
                  clientRefs.current[option.clientname] = el;
                }}
                role="option"
                aria-selected={isOptionSelected(option)}
                onClick={() => selectOption(option)}
                className={`${
                  isOptionSelected(option)
                    ? "bg-unofficial-accent-2"
                    : "hover:bg-bgHoverGrey"
                } self-stretch min-h-8 px-2 py-1.5 rounded-md ani cursor-pointer flex justify-start items-center gap-2 shrink-0`}
              >
                <div className="flex-1 flex justify-start items-center gap-2">
                  <div className="flex-1 flex justify-start items-center gap-1">
                    <div className="flex-1 text-left text-general-foreground text-sm font-normal leading-5 line-clamp-1">
                      {option.clientname}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(ClientSelectDropDown);
